<template>
  <section
    class="table-documents"
    :aria-label="$t('sidebarItem.linkedDocuments')"
    :aria-busy="loading"
  >
    <div class="table-documents__toolbar">
      <div class="table-documents__summary">
        {{ $t('rowLinkedDocuments.title') }}
        <span v-if="!loading && !error" class="table-documents__count">
          {{ filteredDocuments.length }}
        </span>
      </div>
      <div class="form-input table-documents__search">
        <i class="iconoir-search" aria-hidden="true"></i>
        <input
          v-model="search"
          type="search"
          class="form-input__input"
          :aria-label="$t('action.search')"
          :placeholder="$t('action.search')"
        />
      </div>
    </div>
    <div v-if="loading" class="table-documents__state" role="status">
      {{ $t('docsHub.loading') }}
    </div>
    <div v-else-if="error" class="table-documents__state" role="alert">
      <p>{{ $t('common.wrong') }}</p>
      <button
        type="button"
        class="button button--small"
        @click="fetchDocuments()"
      >
        {{ $t('action.retry') }}
      </button>
    </div>
    <div
      v-else-if="!filteredDocuments.length"
      class="table-documents__state"
      role="status"
    >
      {{ $t('workspaceSearch.noResults') }}
    </div>
    <div v-else class="table-documents__scroll">
      <table class="table-documents__list">
        <thead>
          <tr>
            <th scope="col">{{ $t('docsHub.title') }}</th>
            <th scope="col">{{ $t('rowEditModal.itemChip') }}</th>
            <th scope="col">{{ $t('docsHub.lastUpdated') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="document in filteredDocuments" :key="document.id">
            <td>
              <NuxtLink
                class="table-documents__document"
                :to="{
                  name: 'workspace-docs',
                  params: { workspaceId: database.workspace.id },
                  query: { doc: document.id },
                }"
              >
                <i class="iconoir-page" aria-hidden="true"></i>
                <span>{{ document.name || $t('docsHub.untitled') }}</span>
              </NuxtLink>
            </td>
            <td>
              <NuxtLink
                v-if="document.linked_row_id && document.linked_row_name"
                :to="{
                  name: 'database-table-row',
                  params: {
                    databaseId: database.id,
                    tableId: table.id,
                    viewId: '',
                    rowId: document.linked_row_id,
                  },
                }"
              >
                {{ document.linked_row_name }}
              </NuxtLink>
              <span
                v-else-if="document.linked_row_id"
                class="color-neutral-600"
              >
                {{ $t('rowEditModal.itemChip') }} #{{ document.linked_row_id }}
              </span>
              <span v-else class="color-neutral-600">—</span>
            </td>
            <td class="table-documents__date">
              <time v-if="document.updated_on" :datetime="document.updated_on">
                {{ formatUpdatedOn(document.updated_on) }}
              </time>
              <span v-else>—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import DocsService from '@baserow/modules/core/services/docs'

export default {
  name: 'TableLinkedDocuments',
  props: {
    database: { type: Object, required: true },
    table: { type: Object, required: true },
  },
  data() {
    return {
      documents: [],
      search: '',
      loading: true,
      error: false,
      requestId: 0,
    }
  },
  computed: {
    documentScope() {
      return `${this.database.workspace.id}:${this.table.id}`
    },
    filteredDocuments() {
      const query = this.search.trim().toLocaleLowerCase()
      return this.documents.filter((document) =>
        [document.name || this.$t('docsHub.untitled'), document.linked_row_name]
          .filter(Boolean)
          .some((value) => value.toLocaleLowerCase().includes(query))
      )
    },
  },
  watch: {
    documentScope: {
      immediate: true,
      handler() {
        this.search = ''
        this.fetchDocuments()
      },
    },
  },
  beforeUnmount() {
    this.requestId++
  },
  methods: {
    async fetchDocuments() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = false
      this.documents = []
      try {
        const { data } = await DocsService(this.$client).fetchAll(
          this.database.workspace.id,
          { tableId: this.table.id }
        )
        if (requestId === this.requestId) {
          this.documents = data.documents
        }
      } catch {
        if (requestId === this.requestId) {
          this.error = true
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false
        }
      }
    },
    formatUpdatedOn(value) {
      return new Date(value).toLocaleString(this.$i18n.locale, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>
